import React, { useCallback, useEffect, useState } from "react";
import { useActor, useMachine } from "@xstate/react";
import QrScanner from "qr-scanner";
import Dropzone from "react-dropzone";
import InputMask from "react-input-mask";
import { Tooltip } from "react-tooltip";
import { Icon } from "../SVGsprite/svgsprite";
import styles from "./UploadOrder.module.scss";
import { uploadOrderMachine } from "../../xstate/uploadOrder.machine";
import { Loader } from "../Loader/Loader";
import { Table } from "@/js/modules/Table/Table";
import { Link } from "react-router-dom";
import heic2any from "heic2any";
import b64toBlob from "b64-to-blob";
import apiMethods from "../../api/apiMethods";
import { AuthState } from "../../xstate/auth.machine";

// Кастомный Select компонент
const CustomSelect = ({
	options,
	value,
	onChange,
	placeholder,
	error,
	label,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const selectRef = React.useRef(null);

	useEffect(() => {
		const handleClickOutside = event => {
			if (selectRef.current && !selectRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const selectedOption = options.find(opt => opt.value === value);

	return (
		<div className={styles.formInput}>
			{label && <label className={styles.inputLabel}>{label}</label>}
			<div className={styles.customSelect} ref={selectRef}>
				<div
					className={`${styles.selectTrigger} ${isOpen ? styles.open : ""} ${error ? styles.error : ""}`}
					onClick={() => setIsOpen(!isOpen)}
				>
					<span className={styles.selectValue}>
						{selectedOption ? selectedOption.label : placeholder || "Выберите"}
					</span>
					<svg
						width="20"
						height="20"
						viewBox="0 0 20 20"
						fill="none"
						className={isOpen ? styles.rotated : ""}
					>
						<path
							d="M5 7.5L10 12.5L15 7.5"
							stroke="#7E7D84"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</div>

				{isOpen && (
					<div className={styles.selectDropdown}>
						{options.map(option => (
							<div
								key={option.value}
								className={`${styles.selectOption} ${value === option.value ? styles.selected : ""}`}
								onClick={() => {
									onChange(option.value);
									setIsOpen(false);
								}}
							>
								{option.label}
							</div>
						))}
					</div>
				)}
			</div>
			{error && <div className="errorMessage">{error}</div>}
		</div>
	);
};

// Компонент для одного блока масла
const OilBlock = ({
	index,
	data,
	onUpdate,
	onRemove,
	canRemove,
	oilOptions,
	canisterOptions,
	errors,
}) => {
	const handleContainerTypeChange = value => {
		onUpdate(index, {
			...data,
			container_type: value,
			quantity: "",
		});
	};

	const handleQuantityChange = event => {
		let value = event.target.value;
		value = value.replace(/[^\d]/g, "");
		if (value) {
			value = parseInt(value, 10).toString();
		}

		onUpdate(index, {
			...data,
			quantity: value,
		});
	};

	const getCanisterVolume = containerType => {
		if (!containerType || containerType === "pouring") return null;
		const match = containerType.match(/canister_(\d+)l/);
		return match ? match[1] : null; // возвращаем "1", "4", "6", "20"
	};

	const isPouring = data.container_type === "pouring";
	const canisterVolume = getCanisterVolume(data.container_type);

	const getQuantityLabel = () => {
		if (!data.container_type) {
			return "Кол-во";
		}
		if (isPouring) {
			return "Кол-во литров, л.";
		}
		return `Кол-во канистр (${canisterVolume}л), шт.`;
	};

	const getQuantityPlaceholder = () => {
		if (!data.container_type) {
			return "Введите количество";
		}
		if (isPouring) {
			return "Введите количество литров";
		}
		return `Введите количество канистр (${canisterVolume}л)`;
	};

	return (
		<div className={styles.oilBlock}>
			<div className={styles.oilBlockHeader}>
				<span className={styles.oilBlockTitle}>#{index + 1}</span>
				{canRemove && (
					<button
						type="button"
						onClick={() => onRemove(index)}
						className={styles.removeOilBtn}
					>
						<Icon name="icon-close" sizeW="20" sizeH="20" />
					</button>
				)}
			</div>

			<div className={styles.inputsRow}>
				<div className={styles.formInput}>
					<CustomSelect
						label="Масло"
						options={oilOptions}
						value={data.oil_name}
						onChange={value => onUpdate(index, { ...data, oil_name: value })}
						placeholder="Выберите масло"
						error={errors?.oil_name}
					/>
				</div>

				<div className={styles.formInput}>
					<CustomSelect
						label="Тип тары"
						options={canisterOptions}
						value={data.container_type}
						onChange={handleContainerTypeChange}
						placeholder="Выберите тип тары"
						error={errors?.container_type}
					/>
				</div>
			</div>

			<div className={styles.inputsRow}>
				<div className={styles.formInput}>
					<label
						htmlFor={`oil_quantity_${index}`}
						className={styles.inputLabel}
					>
						{getQuantityLabel()}
					</label>
					<div className="inputWrapper">
						<input
							id={`oil_quantity_${index}`}
							name={`oil_quantity_${index}`}
							type="text"
							value={data.quantity || ""}
							onChange={handleQuantityChange}
							placeholder={getQuantityPlaceholder()}
							inputMode="numeric"
							disabled={!data.container_type}
						/>
					</div>
					{errors?.quantity && (
						<div className="errorMessage">{errors.quantity}</div>
					)}
				</div>

				<div className={styles.formInput}>
					<label htmlFor={`oil_sum_${index}`} className={styles.inputLabel}>
						Сумма продажи масла, руб.
					</label>
					<div className="inputWrapper">
						<InputMask
							id={`oil_sum_${index}`}
							name={`oil_sum_${index}`}
							type="text"
							mask="999999.99"
							maskChar=""
							placeholder="0.00"
							value={data.sum}
							onChange={event => {
								onUpdate(index, { ...data, sum: event.target.value });
							}}
						/>
					</div>
					{errors?.sum && <div className="errorMessage">{errors.sum}</div>}
				</div>
			</div>
		</div>
	);
};

export function UploadOrder({ isClosed }) {
	const [stateAuth, sendAuth] = useActor(AuthState);
	const [state, send] = useMachine(uploadOrderMachine);
	const [errorDate, setErrorDate] = useState("");
	const [paths, setPaths] = useState([]);
	const [images, setImages] = useState([]);
	const [inputFields, setInputFields] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);

	if (
		stateAuth.context.type === "seller" &&
		stateAuth.context.service_order_verification_status !== "enabled"
	) {
		console.log("1");
		window.location.href = "/account";
	}
	// Состояние для динамических блоков масла
	const [oilBlocks, setOilBlocks] = useState([
		{
			id: Date.now(),
			oil_name: "",
			container_type: "",
			quantity: "", // только quantity, без liters
			sum: "",
		},
	]);

	// Опции для селектов (акционные масла)
	const oilOptions = [
		{ value: "ZIC TOP", label: "ZIC TOP" },
		{ value: "ZIC ZERO", label: "ZIC ZERO" },
		{ value: "ZIC X9", label: "ZIC X9" },
		{ value: "ZIC X7", label: "ZIC X7" },
		{ value: "ZIC X8", label: "ZIC X8" },
		{ value: "ZIC ULTRA", label: "ZIC ULTRA" },
		{ value: "ZIC X9000", label: "ZIC X9000" },
		{ value: "ZIC X8000", label: "ZIC X8000" },
		{ value: "ZIC X7000", label: "ZIC X7000" },
	];

	// Опции для типа тары
	const canisterOptions = [
		{ value: "pouring", label: "Розлив" },
		{ value: "canister_1l", label: "Канистра, 1л." },
		{ value: "canister_4l", label: "Канистра, 4л." },
		{ value: "canister_6l", label: "Канистра, 6л." },
		{ value: "canister_20l", label: "Канистра, 20л." },
	];

	// Валидация блоков масла (из машины состояний)
	const oilsErrors = state.context.oilsErrors || {};

	const isPdfFile = file => {
		const pdfTypes = ["application/pdf"];
		const pdfExtensions = [".pdf"];

		const isPdfByType = pdfTypes.includes(file.type);
		const isPdfByExtension = pdfExtensions.some(ext =>
			file.name?.toLowerCase().endsWith(ext),
		);

		return isPdfByType || isPdfByExtension;
	};

	const isHeicFile = file => {
		const heicTypes = ["image/heic", "image/heif"];
		const heicExtensions = [".heic", ".heif"];

		const isHeicByType = heicTypes.includes(file.type);
		const isHeicByExtension = heicExtensions.some(ext =>
			file.name?.toLowerCase().endsWith(ext),
		);

		return isHeicByType || isHeicByExtension;
	};

	const convertHeicToJpeg = async file => {
		try {
			const convertedBlob = await heic2any({
				blob: file,
				toType: "image/jpeg",
				quality: 0.8,
			});

			const convertedFile = new File(
				[convertedBlob],
				file.name.replace(/\.(heic|heif)$/i, ".jpg"),
				{ type: "image/jpeg", lastModified: Date.now() },
			);

			return convertedFile;
		} catch (error) {
			console.error("❌ HEIC conversion failed:", error);
			throw error;
		}
	};

	const convertPdfToImage = async file => {
		try {
			const response = await apiMethods.uploadVoucher.convertPdfToImage(file);
			const imagesArray = response.data?.files;

			if (!imagesArray || imagesArray.length === 0) {
				throw new Error("Не удалось получить изображение из PDF");
			}

			const imageData = imagesArray[0];
			let base64String = imageData.base64;

			if (!base64String) {
				throw new Error("Нет данных изображения в ответе сервера");
			}

			if (base64String.includes(",")) {
				base64String = base64String.split(",")[1];
			}

			const mimeType = imageData.mime || "image/jpeg";
			const blob = b64toBlob(base64String, mimeType);

			const imageFile = new File(
				[blob],
				imageData.file_name || file.name.replace(/\.pdf$/i, ".jpg"),
				{ type: mimeType, lastModified: Date.now() },
			);

			return imageFile;
		} catch (error) {
			console.error("❌ PDF conversion failed:", error);
			throw new Error(
				"Не удалось конвертировать PDF. Пожалуйста, попробуйте другой файл.",
			);
		}
	};

	const resizeImageForScanning = (file) => {
		return new Promise((resolve, reject) => {
			const img = new Image();
			const url = URL.createObjectURL(file);

			img.onload = () => {
				let width = img.width;
				let height = img.height;

				// if (width > maxSize || height > maxSize) {
				// 	if (width > height) {
				// 		height = (height * maxSize) / width;
				// 		width = maxSize;
				// 	} else {
				// 		width = (width * maxSize) / height;
				// 		height = maxSize;
				// 	}
				// }

				const canvas = document.createElement("canvas");
				canvas.width = width;
				canvas.height = height;
				const ctx = canvas.getContext("2d");
				ctx.drawImage(img, 0, 0, width, height);

				canvas.toBlob(
					blob => {
						if (blob) {
							const resizedFile = new File([blob], file.name, {
								type: "image/jpeg",
								lastModified: Date.now(),
							});
							URL.revokeObjectURL(url);
							resolve(resizedFile);
						} else {
							reject(new Error("Failed to resize image"));
						}
					},
					"image/jpeg",
					0.9,
				);
			};

			img.onerror = () => {
				URL.revokeObjectURL(url);
				reject(new Error("Failed to load image"));
			};

			img.src = url;
		});
	};

	/* Обработка изменений файлового инпута */
	function handleFileChange(files) {
		const processFiles = async () => {
			setIsProcessing(true); // ВКЛЮЧАЕМ ЛОАДЕР
			const processedFiles = [];

			for (let i = 0; i < files.length; i++) {
				let file = files[i];

				// Конвертация PDF
				if (isPdfFile(file)) {
					try {
						const convertedImage = await convertPdfToImage(file);
						const dataTransfer = new DataTransfer();
						dataTransfer.items.add(convertedImage);
						const newFiles = Array.from(dataTransfer.files);
						handleFileChange(newFiles);
						setIsProcessing(false);
						return;
					} catch (error) {
						console.error("❌ PDF conversion failed:", error);
						alert(error.message);
						continue;
					}
				}
				// Конвертация HEIC
				else if (isHeicFile(file)) {
					try {
						file = await convertHeicToJpeg(file);
						console.log("✅ HEIC converted");
					} catch (error) {
						console.error("❌ HEIC conversion failed:", error);
					}
				}

				// Ресайз изображения
				if (file && !isPdfFile(file)) {
					try {
						file = await resizeImageForScanning(file);
					} catch (error) {
						console.error("❌ Resize failed:", error);
					}
				}

				if (file) {
					processedFiles.push(file);
				}
			}

			if (processedFiles.length === 0) {
				alert("Не удалось обработать ни одного файла");
				setIsProcessing(false);
				return;
			}

			setImages(processedFiles);
			setPaths(processedFiles.map(f => URL.createObjectURL(f)));

			send({
				type: "INPUT",
				data: {
					image: processedFiles,
				},
			});

			setInputFields(true);
			setIsProcessing(false); // ВЫКЛЮЧАЕМ ЛОАДЕР
		};

		processFiles();
	}

	const isAndroid = /Android/i.test(navigator.userAgent);

	const openCamera = () => {
		const input = document.createElement("input");
		input.type = "file";
		input.accept = "image/*";
		input.capture = "environment";
		input.onchange = e => {
			if (e.target.files && e.target.files.length > 0) {
				handleFileChange(Array.from(e.target.files));
			}
		};
		input.click();
	};

	const openFilePicker = () => {
		const input = document.createElement("input");
		input.type = "file";
		input.accept = "image/*";
		input.onchange = e => {
			if (e.target.files && e.target.files.length > 0) {
				handleFileChange(Array.from(e.target.files));
			}
		};
		input.click();
	};

	function deleteImage(index) {
		const newImages = [...images];
		newImages.splice(index, 1);
		setImages(newImages);
		setPaths(newImages.map(file => URL.createObjectURL(file)));

		if (newImages.length === 0) {
			setInputFields(false);
			send({
				type: "INPUT",
				data: {
					image: "",
				},
			});
		} else {
			send({
				type: "INPUT",
				data: {
					image: newImages,
				},
			});
		}
	}

	// Добавление нового блока масла
	const addOilBlock = () => {
		const newBlocks = [
			...oilBlocks,
			{
				id: Date.now(),
				oil_name: "",
				container_type: "",
				quantity: "",
				sum: "",
			},
		];
		setOilBlocks(newBlocks);
		updateOilsInMachine(newBlocks);
	};

	// Обновление блока масла
	const updateOilBlock = (index, updatedData) => {
		const newBlocks = [...oilBlocks];
		newBlocks[index] = { ...newBlocks[index], ...updatedData };
		setOilBlocks(newBlocks);
		updateOilsInMachine(newBlocks);
	};

	// Удаление блока масла
	const removeOilBlock = index => {
		if (oilBlocks.length > 1) {
			const newBlocks = oilBlocks.filter((_, i) => i !== index);
			setOilBlocks(newBlocks);
			updateOilsInMachine(newBlocks);
		}
	};

	// Обновление масел в машине состояний
	const updateOilsInMachine = blocks => {
		const oilsData = blocks
			.map(block => {
				if (
					!block.oil_name ||
					!block.container_type ||
					!block.quantity ||
					!block.sum
				) {
					return null;
				}

				const isPouring = block.container_type === "pouring";
				const inputQuantity = parseInt(block.quantity, 10);

				if (isNaN(inputQuantity) || inputQuantity <= 0) {
					return null;
				}

				let quantity, liters, containerTypeForSend;

				if (isPouring) {
					quantity = 1;
					liters = inputQuantity;
					containerTypeForSend = "pouring";
				} else {
					const match = block.container_type.match(/canister_(\d+)l/);
					const canisterVolume = match ? parseInt(match[1], 10) : null;

					if (!canisterVolume) return null;

					quantity = inputQuantity;
					liters = canisterVolume;
					containerTypeForSend = "canister";
				}

				return {
					oil_name: block.oil_name,
					container_type: containerTypeForSend,
					quantity: quantity,
					liters: liters,
					sum: block.sum,
				};
			})
			.filter(block => block !== null);

		if (oilsData.length > 0) {
			send({
				type: "SET_GOODS",
				data: { goods: oilsData },
			});
		}
	};

	// Синхронизация масел из машины состояний при загрузке
	useEffect(() => {
		if (state.context.goods && state.context.goods.length > 0) {
			const blocks = state.context.goods.map((oil, index) => ({
				id: Date.now() + index,
				oil_name: oil.oil_name || "",
				container_type: oil.container_type || "",
				quantity: oil.quantity || "", // только quantity
				sum: oil.sum || "",
			}));
			setOilBlocks(blocks);
		}
	}, []);

	const debugFormData = formData => {
		console.log("🐛 [DEBUG] Содержимое FormData:");
		for (let pair of formData.entries()) {
			if (pair[0] === "photos[]") {
				console.log(
					`  ${pair[0]}: File - ${pair[1].name} (${pair[1].size} bytes)`,
				);
			} else if (pair[0] === "goods") {
				console.log(`  ${pair[0]}: ${pair[1]}`);
				try {
					console.log(`    Parsed:`, JSON.parse(pair[1]));
				} catch (e) {}
			} else {
				console.log(`  ${pair[0]}: ${pair[1]}`);
			}
		}
	};

	function submitForm(event) {
		event.preventDefault();

		// Лог перед отправкой
		console.log("🚀 [COMPONENT] Отправка формы");
		console.log("  - Номер заказ-наряда:", state.context.number);
		console.log("  - Дата:", state.context.date);
		console.log("  - Количество масел:", oilBlocks.length);
		console.log("  - Есть изображения:", !!state.context.image);
		console.log(
			"  - Количество изображений:",
			state.context.image?.length || 0,
		);

		// Обновляем масла перед отправкой
		updateOilsInMachine(oilBlocks);

		send("SUBMIT_FIELDS");
	}

	useEffect(() => {
		document.title =
			"Зарегистрировать заказ-наряд | Программа лояльности SK ZIC";
	}, []);

	const [isTableVisible, setIsTableVisible] = useState(false);
	const toggleTable = () => {
		setIsTableVisible(!isTableVisible);
	};

	const resetForm = () => {
		setErrorDate("");
		setPaths([]);
		setImages([]);
		setInputFields(false);

		setOilBlocks([
			{
				id: Date.now(),
				oil_name: "",
				container_type: "",
				quantity: "",
				sum: "",
			},
		]);

		send({ type: "ONE_MORE" });
	};

	return (
		<form
			className={styles.uploadingForm}
			onSubmit={event => submitForm(event)}
		>
			{state.matches({ form: "sending_fields" }) && (
				<div className={styles.loading}>
					<Loader />
				</div>
			)}

			{isProcessing && (
				<div className={styles.loading}>
					<Loader />
				</div>
			)}

			<h1>
				{isClosed
					? "Прием заказ-нарядов завершен!"
					: "Зарегистрировать заказ-наряд"}
			</h1>

			{isClosed ? (
				<div style={{ textAlign: "center", marginTop: "30px" }}>
					<p>
						Загруженные заказ-наряды проверим до 18.12 включительно.
						<br /> Заказать сертификаты можно до 22.12 включительно.
					</p>
					<p>Спасибо за участие!</p>
				</div>
			) : (
				<>
					<div className={styles.infoWrapper}>
						<div>
							<p>1. Загрузите заказ-наряд с продукцией ZIC.</p>
							<p>
								2. Заказ-наряд должен быть в формате JPEG, PNG или PDF.
								Заказ-наряд должен быть виден полностью.
							</p>
							<p>
								3. Если документ будет плохого качества — заказ-наряд принят не
								будет.
							</p>
							<p>
								4. Проверка наряд-заказов с ошибками или неполными данными
								занимает больше времени.
							</p>
						</div>
					</div>

					<div className={styles.mb_main}>
						<div
							className={styles.title_table}
							onClick={toggleTable}
							style={{ cursor: "pointer" }}
						>
							В акции участвуют:{" "}
							<span
								style={{
									display: "inline-block",
									transform: isTableVisible ? "rotate(0deg)" : "rotate(180deg)",
									transition: "transform 0.3s ease-out",
								}}
							>
								<img src="/images/arrow.svg" alt="" />
							</span>
						</div>
						<div
							className={styles.table_custom_css}
							style={{
								maxHeight: isTableVisible ? "1000px" : "0",
								overflow: "hidden",
								transition: "max-height 0.3s ease-out",
							}}
						>
							<Table
								overflow="auto"
								headers={[
									{ field: "name", title: "Наименование" },
									{ field: "volume", title: "Единица измерения" },
									{ field: "price", title: "Количество баллов" },
								]}
								tableData={[
									{ name: "ZIC TOP", volume: "1 литр", price: 25 },
									{ name: "ZIC ZERO", volume: "1 литр", price: 25 },
									{ name: "ZIC X9", volume: "1 литр", price: 20 },
									{ name: "ZIC X7", volume: "1 литр", price: 15 },
									{ name: "ZIC X8", volume: "1 литр", price: 20 },
									{ name: "ZIC ULTRA", volume: "1 литр", price: 25 },
									{ name: "ZIC X9000", volume: "1 литр", price: 25 },
									{ name: "ZIC X8000", volume: "1 литр", price: 20 },
									{ name: "ZIC X7000", volume: "1 литр", price: 15 },
								]}
								isLoading={false}
							/>
							<p className={styles.ball_title}>1 балл = 1 рубль</p>
							<p style={{ color: "rgba(0, 0, 0, 0.5)" }}>
								* Наименование продукции может быть изменено
								<br />
								** Количество начисляемых баллов может быть изменено
								<br />В акции участвуют заказ-наряды с датой продажи не ранее 01
								апреля 2026 г.
							</p>{" "}
							<Link
								style={{ color: "rgba(0, 0, 0, 0.5)" }}
								to="https://storage.yandexcloud.net/zictop.public/documents/rules.pdf"
								target="_blank"
							>
								Полные правила
							</Link>
						</div>
					</div>

					{state.matches({ form: "failure" }) && (
						<>
							{state.context.errors.map((item, index) => (
								<div className="errorMessage" key={index}>
									{item}
								</div>
							))}
						</>
					)}

					{state.matches({ form: "success" }) && (
						<div className={styles.successMessage}>
							<div className={styles.successContent}>
								<h2>
									Загрузка заказ-наряда <br /> прошла успешно
								</h2>
								<Icon
									name="icon-success"
									className={styles.iconSuccess}
									sizeW="100"
									sizeH="100"
								/>
								<div
									style={{ marginTop: "20px" }}
									className={"btn btnPrimary btnIcon newBtnUpdate"}
									onClick={resetForm}
								>
									Загрузить ещё
								</div>
							</div>
						</div>
					)}

					<div className={styles.dropzoneWrapper}>
						{paths.length < 1 && (
							<Dropzone
								className={styles.scanner}
								onDrop={acceptedFiles => handleFileChange(acceptedFiles)}
							>
								{({ getRootProps, getInputProps }) => (
									<section>
										<div {...getRootProps()}>
											<input {...getInputProps()} />
											<div className={styles.scanner}>
												{!isAndroid ? (
													<p>
														Перетащите документ сюда или сделайте фото
														заказ-наряда
													</p>
												) : (
													<p>Загрузите изображение заказ-наряда</p>
												)}
												<div className="formRow">
													{isAndroid ? (
														<div
															style={{
																gap: "10px",
																display: "flex",
																alignItems: "center",
																flexDirection: "column",
															}}
														>
															<button
																type="button"
																className="btn btnPrimary btnForm newBtnUpdate"
																onClick={e => {
																	e.stopPropagation();
																	openCamera();
																}}
															>
																Сделать фото
															</button>
															<button
																type="button"
																className="btn btnPrimary btnForm"
																onClick={e => {
																	e.stopPropagation();
																	openFilePicker();
																}}
															>
																Выбрать из галереи
															</button>
														</div>
													) : (
														<button
															type="button"
															className="btn btnPrimary btnForm"
														>
															Добавить фото
														</button>
													)}
												</div>
											</div>
										</div>
									</section>
								)}
							</Dropzone>
						)}

						{paths.length > 0 && (
							<>
								{paths.map((item, index) => (
									<span className={styles.imageWrapper} key={index}>
										<div
											onClick={() => deleteImage(index)}
											className={styles.close}
										>
											<Icon name="icon-close" sizeW="24" sizeH="24" />
										</div>
										<img src={item} className={styles.image} alt="" />
									</span>
								))}
							</>
						)}
					</div>

					{inputFields === true && (
						<div className={styles.fieldsWrapper}>
							<div className={styles.fields}>
								<p className={styles.fieldsTitle}>
									Введите данные по заказ-наряду
								</p>

								<div className={styles.inputsRow}>
									<div className={styles.formInput}>
										<label htmlFor="number" className={styles.inputLabel}>
											Номер заказ-наряда
										</label>
										<div className="inputWrapper">
											<InputMask
												id="number"
												name="number"
												type="text"
												maskChar=""
												value={state.context.number || ""}
												onChange={event => {
													send({
														type: "INPUT",
														data: {
															number: event.target.value,
														},
													});
												}}
											/>
										</div>
										{state.context.orderNumberError && (
											<div className="errorMessage" aria-live="polite">
												<span className="visuallyHidden">
													Ошибка в поле Номер заказ-наряда:
												</span>
												{state.context.orderNumberError}
											</div>
										)}
									</div>

									<div className={styles.formInput}>
										<label
											htmlFor="creation_date"
											className={styles.inputLabel}
										>
											Дата покупки
										</label>
										<div className="inputWrapper">
											<InputMask
												id="creation_date"
												name="creation_date"
												type="text"
												mask="99.99.9999"
												maskChar=""
												placeholder="ДД.ММ.ГГГГ"
												value={state.context.date || ""}
												onChange={event => {
													setErrorDate("");
													const inputDate = event.target.value;
													const [day, month, year] = inputDate
														.split(".")
														.map(Number);
													const inputDateObj = new Date(year, month - 1, day);
													const currentDate = new Date();
													const cutoffDate = new Date(2026, 3, 1);

													if (
														inputDateObj < cutoffDate ||
														inputDateObj > currentDate
													) {
														setErrorDate(
															`Дата должна быть не ранее 01.04.2026 и не позже текущей даты. (Подробнее в п. 2.1.7 <a
                    href="https://storage.yandexcloud.net/zictop.public/documents/rules.pdf"
                    target='__blank'
                >Правил</a>)`,
														);
													}
													send({
														type: "INPUT",
														data: {
															date: event.target.value,
														},
													});
												}}
											/>
										</div>
										{errorDate && (
											<div
												className="errorMessage"
												aria-live="polite"
												dangerouslySetInnerHTML={{ __html: errorDate }}
											/>
										)}
										{state.context.dateError && (
											<div className="errorMessage" aria-live="polite">
												<span className="visuallyHidden">
													Ошибка в поле дата покупки:
												</span>
												{state.context.dateError}
											</div>
										)}
									</div>
								</div>

								{/* Динамические блоки масла */}
								{oilBlocks.map((block, index) => (
									<OilBlock
										key={block.id}
										index={index}
										data={block}
										onUpdate={updateOilBlock}
										onRemove={removeOilBlock}
										canRemove={index > 0}
										oilOptions={oilOptions}
										canisterOptions={canisterOptions}
										errors={oilsErrors[index]}
									/>
								))}

								<div className={styles.addOilButton} onClick={addOilBlock}>
									<Icon name="icon-add" sizeW="24" sizeH="24" />
									<span>Добавить масло</span>
								</div>
							</div>
						</div>
					)}

					{inputFields === true && (
						<div className="formRow">
							<button
								type="submit"
								disabled={
									errorDate !== "" ||
									!state.context.number ||
									!state.context.date ||
									!state.context.image ||
									oilBlocks.some(
										block =>
											!block.oil_name ||
											!block.container_type ||
											!block.quantity ||
											!block.sum,
									)
								}
								className="btn btnPrimary btnForm"
							>
								Отправить заказ-наряд
							</button>
						</div>
					)}
				</>
			)}
		</form>
	);
}
